import type { SessionConfig } from "@/types"

/*
 * The lobby's session templates.
 *
 */

/**
 * A stroke icon as a handful of primitive shapes rather than one path: the set
 * needs circles (a branching tree, a target) that a single `<path>` can only
 * fake with arc commands. `Glyph` renders whichever shapes are listed, in
 * order, inside one `<svg>`.
 */
export type IconShape =
  | { kind: "path"; d: string }
  | { kind: "circle"; cx: number; cy: number; r: number }
  | { kind: "rect"; x: number; y: number; width: number; height: number; rx?: number }

export interface TemplateIcon {
  viewBox: string
  shapes: readonly IconShape[]
}

export interface SessionTemplate {
  key: "full" | "quick" | "exec"
  name: string
  blurb: string
  icon: TemplateIcon
  config: Partial<SessionConfig>
}

export const TEMPLATES: readonly SessionTemplate[] = [
  {
    key: "full",
    name: "Full discovery",
    blurb: "Every exercise unlocked, deep dive included.",
    // A root node branching down a spine to three leaves — every exercise, all unlocked.
    icon: {
      viewBox: "0 0 24 24",
      shapes: [
        { kind: "rect", x: 2, y: 2, width: 6, height: 6, rx: 2 },
        { kind: "path", d: "M8 5h7M11 5v11a3 3 0 0 0 3 3h1M11 12h4" },
        { kind: "rect", x: 15, y: 2, width: 6, height: 6, rx: 2 },
        { kind: "rect", x: 15, y: 9, width: 6, height: 6, rx: 2 },
        { kind: "rect", x: 15, y: 16, width: 6, height: 6, rx: 2 },
      ],
    },
    config: {
      painsPerParticipant: 3,
      votesPerParticipant: 2,
      handCap: 5,
      mustHaveCap: 3,
      painsEnabled: true,
      userStoryEnabled: true,
      deepDiveEnabled: true,
      roundDurations: { lobby: 5, pains: 10, job: 8, cards: 15, priorities: 10, deep: 15, wrap: 5 },
    },
  },
  {
    key: "quick",
    name: "Quick read",
    blurb: "45 minutes. The top-voted pain becomes the problem, no framing round.",
    icon: {
      viewBox: "0 0 24 24",
      shapes: [
        // A route: start pin, one dog-leg, end pin — the short way through.
        { kind: "path", d: "M10 5h3a2 2 0 0 1 1.6 3.2l-5.2 6.6A2 2 0 0 0 11 19h3" },
        { kind: "circle", cx: 6.5, cy: 6.5, r: 2.5 },
        { kind: "circle", cx: 6.5, cy: 6.5, r: 0.6 },
        { kind: "rect", x: 15, y: 15, width: 5, height: 5, rx: 1.3 },
        { kind: "rect", x: 16.8, y: 16.8, width: 1.4, height: 1.4, rx: 0.4 },
      ],
    },
    config: {
      painsPerParticipant: 2,
      votesPerParticipant: 2,
      handCap: 3,
      mustHaveCap: 3,
      painsEnabled: true,
      userStoryEnabled: false,
      deepDiveEnabled: false,
      /*
       * "No framing round" in the blurb is the prototype's wording for what
       * `userStoryEnabled: false` does here: the Job round still happens, but
       * its one field arrives pre-filled with the winning pain, so it costs a
       * read-back rather than a drafting exercise. Hence job 3, not job 8.
       * lobby 4 + pains 10 + job 3 + cards 15 + priorities 8 + wrap 5 = 45 min.
       */
      roundDurations: { lobby: 4, pains: 10, job: 3, cards: 15, priorities: 8, deep: 12, wrap: 5 },
    },
  },
  {
    key: "exec",
    name: "Exec sprint",
    blurb: "28 minutes. Straight to the deck — no pain points, no framing.",
    icon: {
      viewBox: "0 0 16 16",
      shapes: [
        { kind: "circle", cx: 8, cy: 8, r: 2.8 },
        { kind: "circle", cx: 8, cy: 8, r: 1 },
        { kind: "path", d: "M8 1.8v1.9M8 12.3v1.9M1.8 8h1.9M12.3 8h1.9" },
      ],
    },
    config: {
      painsPerParticipant: 2,
      votesPerParticipant: 1,
      handCap: 3,
      mustHaveCap: 2,
      painsEnabled: false,
      userStoryEnabled: false,
      deepDiveEnabled: false,
      /*
       * Pains and the deep dive are off, and the Job round is a read-back:
       * lobby 3 + job 3 + cards 12 + priorities 7 + wrap 3 = 28 min, which is
       * the number the blurb promises. The prototype reaches the same 28 by
       * dropping its framing round outright; app-v1 keeps the round, because
       * every pick and placement hangs off a job document.
       */
      roundDurations: { lobby: 3, pains: 8, job: 3, cards: 12, priorities: 7, deep: 12, wrap: 3 },
    },
  },
]

/** A gear, for the Custom tile — the rules being tuned by hand rather than picked. */
export const CUSTOM_ICON: TemplateIcon = {
  viewBox: "0 0 24 24",
  shapes: [
    {
      kind: "path",
      d: "M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z",
    },
    { kind: "circle", cx: 12, cy: 12, r: 3 },
  ],
}

export const CUSTOM_BLURB = "Your own mix. Tune the rounds, limits and exercises below."

/**
 * Whether a template is the one in effect: every key it names already matches.
 * Keys it does not name are free, which is what makes `exec` stay selected while
 * the durations are tuned underneath it.
 */
export function templateApplies(template: SessionTemplate, config: SessionConfig): boolean {
  return Object.entries(template.config).every(([key, value]) => {
    if (key === "roundDurations") {
      return Object.entries(value as Record<string, number>).every(
        ([round, minutes]) =>
          config.roundDurations[round as keyof typeof config.roundDurations] === minutes
      )
    }
    return config[key as keyof SessionConfig] === value
  })
}
